import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Download, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Language } from '../App';
interface JanFeb2026IssuePageProps {
  language: Language;
}
export function JanFeb2026IssuePage({
  language
}: JanFeb2026IssuePageProps) {
  const isPashto = language === 'pashto';
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-50px' as const
    },
    transition: {
      duration: 0.6
    }
  };
  return <div className="pt-20 md:pt-24 bg-gray-50 min-h-screen pb-0" data-id="element-561">
      {/* Section 1: Issue Header */}
      <section className="relative bg-[#1a3a6c] py-8 md:py-12 px-6 overflow-hidden" data-id="element-562">
        {/* Subtle bird graphic */}
        <svg className="absolute right-0 top-0 w-1/2 h-full pointer-events-none opacity-5" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMaxYMid meet" viewBox="0 0 1200 400" data-id="element-563">
          <g fill="white" data-id="element-564">
            <path d="M820,145 C810,130 795,120 780,115 C790,125 795,135 800,142 C790,138 775,132 760,130 C778,138 790,142 800,145 C795,148 790,155 785,165 C795,155 800,148 805,145 C810,148 815,155 820,165 C815,155 812,148 810,145 C820,142 835,138 850,130 C835,132 820,138 810,142 C815,135 820,125 830,115 Z" transform="translate(200,50) scale(1.5)" data-id="element-565" />
          </g>
        </svg>

        <div className="relative z-10 max-w-4xl mx-auto flex flex-col items-center text-center" data-id="element-566">
          {/* Logo */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5
        }} className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden border-2 border-white/20 shadow-xl mb-6 flex-shrink-0 bg-white" data-id="element-567">
            <img src="https://cdn.magicpatterns.com/uploads/a1wFSzqfrcuN2xyXncDR6P/Pasoon.png" alt="Pasoon Logo" className="w-full h-full object-contain" data-id="element-568" />
          </motion.div>

          {/* Title */}
          <motion.h1 initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className={`text-3xl md:text-5xl font-bold text-white mb-2 ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-569">
            {isPashto ? 'جنوري – فبروري ۲۰۲۶' : 'January – February 2026'}
          </motion.h1>

          {/* Subtext */}
          <motion.p initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className={`text-white/70 text-lg md:text-xl font-medium tracking-wide ${isPashto ? 'font-pashto' : ''}`} data-id="element-570">
            {isPashto ? 'ټوک ۲۱' : 'Volume 21'}
          </motion.p>

          {/* Color Strip */}
          <motion.div initial={{
          opacity: 0,
          scaleX: 0
        }} animate={{
          opacity: 1,
          scaleX: 1
        }} transition={{
          duration: 0.7,
          delay: 0.3
        }} className="flex w-48 md:w-64 h-1.5 mt-8 rounded-full overflow-hidden" data-id="element-571">
            <div className="flex-1 bg-[#C62828]" data-id="element-572"></div>
            <div className="flex-1 bg-[#F9A825]" data-id="element-573"></div>
            <div className="flex-1 bg-[#2E7D32]" data-id="element-574"></div>
          </motion.div>
        </div>
      </section>

      {/* Section 2: Featured Article Block */}
      <section className="py-8 md:py-12 px-6" data-id="element-575">
        <div className="max-w-3xl mx-auto" data-id="element-576">
          <motion.div {...fadeIn} className="bg-white rounded-2xl shadow-xl shadow-gray-200/40 border border-gray-100 p-8 md:p-12" data-id="element-577">
            <h2 className={`text-2xl md:text-3xl font-bold text-pasoon-dark text-center mb-8 ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-578">
              {isPashto ? 'د خپلواکې پښتو ژبې او پښتني کلتور ټولنه' : 'Independent Pashto Language & Pashtun Association'}
            </h2>

            <div className="space-y-8" data-id="element-579">
              {isPashto ? <>
                  {/* Pashto First */}
                  <div className="text-right" dir="rtl" data-id="element-580">
                    <p className="text-gray-700 font-pashto leading-loose text-lg mb-4" data-id="element-581">
                      د پښتو ژبې وده ټولنیز ژمنتیا، کلتوري ویاړ، تعلیمي پرمختګ،
                      چاپیریالي ملاتړ او مالي سرچینو پورې اړه لري. له هر څه
                      پورته، دا یووالي او دوامداره هڅو ته اړتیا لري.
                    </p>
                    <p className="text-gray-700 font-pashto leading-loose text-lg" data-id="element-582">
                      یوه خپلواکه او ټولنه پر بنسټ ټولنه کولی شي جوړښت، شفافیت
                      او اوږدمهاله لید چمتو کړي. دا به ډاډ ورکړي چې پښتو ژبه او
                      پښتني کلتور به وده وکړي، تطبیق شي او د راتلونکو نسلونو
                      لپاره ودانه شي.
                    </p>
                  </div>
                  <div className="w-full h-px bg-gray-200" data-id="element-583"></div>
                  {/* English Second */}
                  <div className="text-left" dir="ltr" data-id="element-584">
                    <p className="text-gray-600 leading-relaxed text-base mb-4" data-id="element-585">
                      The growth of Pashto language depends on social
                      commitment, cultural pride, educational development,
                      environmental support, and financial resources. Above all,
                      it requires unity and continuous effort.
                    </p>
                    <p className="text-gray-600 leading-relaxed text-base" data-id="element-586">
                      An independent and community-based association can provide
                      structure, transparency, and long-term vision. This will
                      ensure that Pashto language and Pashtun culture continue
                      to grow, adapt, and flourish for future generations.
                    </p>
                  </div>
                </> : <>
                  {/* English First */}
                  <div className="text-left" dir="ltr" data-id="element-587">
                    <p className="text-gray-700 leading-relaxed text-lg mb-4" data-id="element-588">
                      The growth of Pashto language depends on social
                      commitment, cultural pride, educational development,
                      environmental support, and financial resources. Above all,
                      it requires unity and continuous effort.
                    </p>
                    <p className="text-gray-700 leading-relaxed text-lg" data-id="element-589">
                      An independent and community-based association can provide
                      structure, transparency, and long-term vision. This will
                      ensure that Pashto language and Pashtun culture continue
                      to grow, adapt, and flourish for future generations.
                    </p>
                  </div>
                  <div className="w-full h-px bg-gray-200" data-id="element-590"></div>
                  {/* Pashto Second */}
                  <div className="text-right" dir="rtl" data-id="element-591">
                    <p className="text-gray-600 font-pashto leading-loose text-base mb-4" data-id="element-592">
                      د پښتو ژبې وده ټولنیز ژمنتیا، کلتوري ویاړ، تعلیمي پرمختګ،
                      چاپیریالي ملاتړ او مالي سرچینو پورې اړه لري. له هر څه
                      پورته، دا یووالي او دوامداره هڅو ته اړتیا لري.
                    </p>
                    <p className="text-gray-600 font-pashto leading-loose text-base" data-id="element-593">
                      یوه خپلواکه او ټولنه پر بنسټ ټولنه کولی شي جوړښت، شفافیت
                      او اوږدمهاله لید چمتو کړي. دا به ډاډ ورکړي چې پښتو ژبه او
                      پښتني کلتور به وده وکړي، تطبیق شي او د راتلونکو نسلونو
                      لپاره ودانه شي.
                    </p>
                  </div>
                </>}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 3: Cover Image Display */}
      <section className="py-8 md:py-10 px-6 bg-gray-100" data-id="element-594">
        <div className="max-w-4xl mx-auto text-center" data-id="element-595">
          <motion.div {...fadeIn} className="inline-block bg-white p-3 md:p-4 rounded-xl shadow-lg shadow-gray-300/50 border border-gray-200" data-id="element-596">
            <img src="https://cdn.magicpatterns.com/uploads/nkxSFVNX8PortnXbKmnDDH/image.png" alt="Pasoon January-February 2026 Cover" className="w-full max-w-2xl h-auto object-contain rounded" data-id="element-597" />
          </motion.div>
          <motion.p {...fadeIn} className={`mt-6 text-gray-500 text-sm md:text-base font-medium ${isPashto ? 'font-pashto' : ''}`} data-id="element-598">
            {isPashto ? 'د برتانوي کتابتون، لندن' : 'The British Library, London'}
          </motion.p>
        </div>
      </section>

      {/* Section 5: Call to Action */}
      <section className="py-8 md:py-12 px-6 bg-white" data-id="element-599">
        <div className="max-w-3xl mx-auto" data-id="element-600">
          <motion.div {...fadeIn} className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-6" data-id="element-601">
            <button className={`w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-4 bg-pasoon-red text-white rounded-xl font-bold text-lg hover:bg-red-800 transition-all shadow-lg shadow-red-900/20 active:scale-[0.98] ${isPashto ? 'font-pashto' : ''}`} data-id="element-602">
              <BookOpen className="w-5 h-5" data-id="element-603" />
              {isPashto ? 'بشپړه ګڼه ولولئ' : 'Read Full Issue'}
            </button>

            <button className={`w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-4 bg-white border-2 border-gray-200 text-gray-700 rounded-xl font-bold text-lg hover:border-gray-300 hover:bg-gray-50 transition-all active:scale-[0.98] ${isPashto ? 'font-pashto' : ''}`} data-id="element-604">
              <Download className="w-5 h-5" data-id="element-605" />
              {isPashto ? 'PDF ډاونلوډ کړئ' : 'Download PDF'}
            </button>

            <Link to="/subscription" className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-4 text-pasoon-muted font-bold text-lg hover:text-green-800 transition-colors ${isPashto ? 'font-pashto' : ''}`} data-id="element-606">
              <Heart className="w-5 h-5" data-id="element-607" />
              {isPashto ? 'پاڅون سره مرسته وکړئ' : 'Support Pasoon'}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Section 4: Issue Information Footer Block */}
      <section className="bg-[#1a1a1a] relative" data-id="element-608">
        {/* Color Strip */}
        <div className="flex w-full h-1.5" data-id="element-609">
          <div className="flex-1 bg-[#C62828]" data-id="element-610"></div>
          <div className="flex-1 bg-[#F9A825]" data-id="element-611"></div>
          <div className="flex-1 bg-[#2E7D32]" data-id="element-612"></div>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-10 md:py-12" data-id="element-613">
          <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-8" data-id="element-614">
            <div className="text-center md:text-start space-y-3" data-id="element-615">
              <p className={`text-white/90 font-bold text-lg ${isPashto ? 'font-pashto' : ''}`} data-id="element-616">
                {isPashto ? 'خپرندوی: پاڅون خپرندویه ادارې، مردان' : 'Published by: Pasoon Publishers, Mardan'}
              </p>
              <div className="space-y-1 text-white/60 text-sm" data-id="element-617">
                <p data-id="element-618">Tel & Fax: 0937 – 874489</p>
                <p data-id="element-619">Email: pasoonmardan@gmail.com</p>
                <p data-id="element-620">Website: www.pasoon.org</p>
              </div>
            </div>

            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white/10 bg-white flex-shrink-0" data-id="element-621">
              <img src="https://cdn.magicpatterns.com/uploads/a1wFSzqfrcuN2xyXncDR6P/Pasoon.png" alt="Pasoon Logo" className="w-full h-full object-contain" data-id="element-622" />
            </div>
          </div>
        </div>
      </section>
    </div>;
}